// src/edu-world/systems/progression-wiring.ts
// Wire Progression_System into Result_Handler via EventBus
// Standalone module (Phase 3) — pure logic, Phaser-agnostic
// On mission completion → update progression + apply immediate visible zone change
// Requirements: 9.5, 10.5

import type { EventBus } from '../core/event-bus';
import type { MinigameCompleteEvent } from '../types/events';
import type { ProgressionSystem, ProgressionArea, WorldEvolutionEvent } from './progression-system';

// ─── Types ───

export interface MissionAreaMapping {
  /** Mission ID */
  missionId: string;
  /** Which progression area this mission contributes to */
  area: ProgressionArea;
}

export interface ProgressionWiringConfig {
  /** Mappings from mission IDs to their progression areas */
  missionAreaMappings: MissionAreaMapping[];
  /** Default area to use if a mission has no explicit mapping */
  defaultArea: ProgressionArea;
  /** Callback invoked when immediate visible zone change should be applied (within 2 seconds) */
  onImmediateZoneChange?: (missionId: string, area: ProgressionArea) => void;
  /** Callback invoked when a world evolution event is triggered (threshold reached) */
  onEvolutionEvent?: (events: WorldEvolutionEvent[]) => void;
}

// ─── Default Configuration ───

const DEFAULT_CONFIG: ProgressionWiringConfig = {
  missionAreaMappings: [
    { missionId: 'rosa-contar-flores-01', area: 'environment' },
  ],
  defaultArea: 'community',
};

// ─── Wiring Function ───

/**
 * Wire the ProgressionSystem into the event bus so that mission completions
 * automatically update progression tracking.
 *
 * Behavior:
 * - Subscribes to 'minigame:complete' events
 * - On success → calls ProgressionSystem.recordMissionComplete with the correct area
 * - Triggers immediate visible zone change callback within 2 seconds (regardless of threshold)
 * - If threshold is reached → triggers evolution event callback
 *
 * @param progressionSystem - The ProgressionSystem instance to update
 * @param eventBus - The EventBus to subscribe to
 * @param config - Optional configuration for area mappings and callbacks
 * @returns A cleanup function that unsubscribes the event handler
 */
export function wireProgressionToResults(
  progressionSystem: ProgressionSystem,
  eventBus: EventBus,
  config?: Partial<ProgressionWiringConfig>
): () => void {
  const fullConfig: ProgressionWiringConfig = { ...DEFAULT_CONFIG, ...config };

  const handler = (event: MinigameCompleteEvent): void => {
    // Only process successful mission completions
    if (!event.success) {
      return;
    }

    const missionId = event.mission_id;
    const area = resolveArea(missionId, fullConfig);

    // Record mission completion in the progression system
    const evolutionEvents = progressionSystem.recordMissionComplete(area, missionId);

    // Apply immediate visible zone change (within 2 seconds, regardless of threshold)
    // This ensures the child always sees something change after completing a mission
    if (fullConfig.onImmediateZoneChange) {
      // Use setTimeout to ensure this happens within the 2-second window
      // but after the current event processing completes
      setTimeout(() => {
        fullConfig.onImmediateZoneChange!(missionId, area);
      }, 0);
    }

    // If threshold was reached, notify about evolution events
    if (evolutionEvents.length > 0 && fullConfig.onEvolutionEvent) {
      fullConfig.onEvolutionEvent(evolutionEvents);
    }
  };

  // Subscribe to minigame completion events
  eventBus.on('minigame:complete', handler);

  // Return cleanup function to unsubscribe
  return () => {
    eventBus.off('minigame:complete', handler);
  };
}

// ─── Private Helpers ───

/**
 * Resolve which progression area a mission contributes to.
 * Checks explicit mappings first, falls back to default area.
 */
function resolveArea(
  missionId: string,
  config: ProgressionWiringConfig
): ProgressionArea {
  const mapping = config.missionAreaMappings.find((m) => m.missionId === missionId);
  return mapping?.area ?? config.defaultArea;
}
